/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-25 17:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Page, PageActions, FormLayout, Card } from '@shopify/polaris';
import { connect } from 'react-redux';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field, reduxForm } from 'redux-form';

import MainMenu from 'src/components/containers/main-menu';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import history from 'src/routes/history';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { isEmailOpt } from 'src/helpers/form-validation-helper';
import * as optionEmailAction from 'actions/dashboard/option-email-action';

const isEmailOptValue = isEmailOpt();

type Props = {
  i18n: any,
  sendOptEmailLoading: boolean,
  tenantApp: Object,
  handleSubmit: Function,
  pristine: boolean,
  valid: boolean,
  dirty: boolean
};

type State = {};

type ComposedProps = Props & WithI18nProps;

class AccountDetailForm extends Component<ComposedProps, State> {
  onSave = data => {
    const { addOptionEmail } = this.props;
    addOptionEmail({
      optEmail: data.optEmail
    });
  };

  handleDefaultSubmit = e => {
    e.preventDefault();
  };

  render() {
    let { i18n, sendOptEmailLoading, tenantApp, handleSubmit, pristine, valid, dirty } = this.props;

    return (
      <div className="BC-Page BC-Page__AccountDetail">
        <MainMenu menuId="menu-account-details" />
        <Page
          title={i18n.translate('AdminHTML.AccountDetails.title')}
          primaryAction={{
            content: i18n.translate('AdminHTML.Common.Action.save'),
            disabled: pristine || sendOptEmailLoading || !valid,
            onAction: handleSubmit(this.onSave.bind(this)),
            loading: sendOptEmailLoading
          }}>
          <div className="account-details-wrapper">
            <form onSubmit={this.handleDefaultSubmit}>
              <div className="item" style={{ borderBottom: '1px solid #E1E3E5' }}>
                <div className="left">
                  <div className="title">{i18n.translate('AdminHTML.AccountDetails.store')}</div>
                  <div className="description">
                    {i18n.translate('AdminHTML.AccountDetails.store_detail')}
                  </div>
                </div>
                <div className="right">
                  <Card>
                    <FormLayout>
                      <Field
                        name="tenantId"
                        label={i18n.translate('AdminHTML.AccountDetails.store_label')}
                        component={RenderTextField}
                        placeholder={i18n.translate(
                          'AdminHTML.InstallationV2.Onboarding.emailopt_hint'
                        )}
                        disabled={true}
                        value={tenantApp.tenantId}
                        anotherProps={{ disabled: true }}
                      />
                    </FormLayout>
                  </Card>
                </div>
              </div>
              <div className="item">
                <div className="left">
                  <div className="title">
                    {i18n.translate('AdminHTML.AccountDetails.contactEmail')}
                  </div>
                  <div className="description">
                    {i18n.translate('AdminHTML.AccountDetails.contactEmail_detail')}
                  </div>
                </div>
                <div className="right">
                  <Card>
                    <FormLayout>
                      <Field
                        name="optEmail"
                        label={i18n.translate('AdminHTML.AccountDetails.contactEmail_label')}
                        component={RenderTextField}
                        placeholder={i18n.translate(
                          'AdminHTML.InstallationV2.Onboarding.emailopt_hint'
                        )}
                        value={tenantApp.optEmail}
                        validate={[isEmailOptValue]}
                      />
                    </FormLayout>
                  </Card>
                </div>
              </div>
            </form>
          </div>
          <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.Common.Action.save'),
              disabled: pristine || sendOptEmailLoading || !valid,
              onAction: handleSubmit(this.onSave.bind(this)),
              loading: sendOptEmailLoading
            }}
          />
          {/* This modal is used for when click any element on this page, but route not change */}
          {/* <ConfirmLeaveModal
            active={activeModalConfirmLeavePage}
            handleCancel={this.closeModal}
            handleDiscard={this.handleDiscard}
          /> */}

          {/* This modal is used for when click any link on this page, the route will change */}
          <RouteLeavingGuard
            when={dirty}
            navigate={path => {
              history.push(path);
            }}
            isGotoWithoutShowModalConfirm={false}
            shouldBlockNavigation={dirty}
          />
        </Page>
      </div>
    );
  }

  // Handle change tab
  handleTabChange = (selectedTabIndex: number) => {
    this.setState({ selected: selectedTabIndex });
  };
}

const AccountDetail = reduxForm({
  form: 'AccountDetailForm', // a unique identifier for this form
  enableReinitialize: true
})(AccountDetailForm);

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const optEmail = tenantApp.optEmail || tenantApp.externalInfo?.email;

  return {
    tenantApp,
    sendOptEmailLoading: _.get(state, 'optionEmail.loading'),
    sendOptEmailSuccess: _.get(state, 'optionEmail.success'),
    initialValues: {
      tenantId: tenantApp.tenantId,
      optEmail
    }
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  addOptionEmail: (body: Object) => dispatch(optionEmailAction.addOptionEmail(body, ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(AccountDetail));
